import React from 'react';
import type { ComponentFieldDefinition } from '@troia-v3/types';
import { FormField } from '@/shared/components/ui/FormField';
import { DynamicFieldRenderer } from '../DynamicFieldRenderer';
import { Icon } from '@/shared/icons'
interface ListFieldEditorProps {
  field: ComponentFieldDefinition;
  value: Record<string, unknown>[];
  onChange: (value: unknown) => void;
  allValues: Record<string, unknown>;
  onChangeAll: (name: string, value: unknown) => void;
}

/**
 * Editor for 'list' field type. Renders a sortable list of sub-objects,
 * each rendered by DynamicFieldRenderer using subFields schema.
 */
export const ListFieldEditor: React.FC<ListFieldEditorProps> = ({
  field,
  value,
  onChange,
}) => {
  const items = value || [];
  const subFields = field.subFields || [];

  const addItem = () => {
    const defaults: Record<string, unknown> = {};
    for (const sf of subFields) {
      if (sf.defaultValue !== undefined) {
        defaults[sf.name] = sf.defaultValue;
      }
    }
    onChange([...items, defaults]);
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, name: string, val: unknown) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [name]: val };
    onChange(updated);
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    const updated = [...items];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onChange(updated);
  };

  return (
    <FormField label={field.label} description={field.description}>
      <div className="space-y-3">
        {items.map((item, index) => (
          <div
            key={index}
            className="rounded-lg border p-4"
            style={{
              borderColor: 'var(--theme-card-border)',
              backgroundColor: 'var(--theme-card-background)',
            }}
          >
            {/* Item header */}
            <div className="flex items-center justify-between mb-3">
              <span
                className="text-xs font-medium"
                style={{ color: 'var(--theme-text-secondary)' }}
              >
                Item {index + 1}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => moveItem(index, 'up')}
                  disabled={index === 0}
                  className="p-1 rounded hover:opacity-70 disabled:opacity-30"
                  style={{ color: 'var(--theme-text-secondary)' }}
                >
                  <Icon name="chevron-up" className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => moveItem(index, 'down')}
                  disabled={index === items.length - 1}
                  className="p-1 rounded hover:opacity-70 disabled:opacity-30"
                  style={{ color: 'var(--theme-text-secondary)' }}
                >
                  <Icon name="chevron-down" className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="p-1 rounded hover:opacity-70"
                  style={{ color: 'var(--theme-error-text, #ef4444)' }}
                >
                  <Icon name="trash" className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Item fields */}
            <DynamicFieldRenderer
              fields={subFields}
              values={item}
              onChange={(name, val) => updateItem(index, name, val)}
              groupLabel={`${field.label} ${index + 1}`}
            />
          </div>
        ))}

        {/* Add button */}
        <button
          type="button"
          onClick={addItem}
          className="w-full py-2 rounded-lg border-2 border-dashed flex items-center justify-center gap-2 text-sm font-medium transition-colors hover:opacity-80"
          style={{
            borderColor: 'var(--theme-card-border)',
            color: 'var(--theme-text-secondary)',
          }}
        >
          <Icon name="plus" className="w-4 h-4" />
          Adicionar {field.label}
        </button>
      </div>
    </FormField>
  );
};
